/**
 * Judge endpoint validation. Rejects anything that could silently redirect
 * verdicts: non-https, embedded credentials, or hosts outside the trusted
 * allowlist. A self-hosted judge is allowed only when it also supplies a
 * response-signing pubkey so the SDK can detect a compromised judge.
 */
import { DEFAULT_ENDPOINT } from "./constants.js";

export type JudgeEndpointConfig =
  | { policy?: "default"; endpoint?: string }
  | { policy: "self-hosted"; endpoint: string; verifyPubKey: string };

export interface ValidatedEndpoint {
  url: string;
  policy: "default" | "self-hosted";
  verifyPubKey: string | null;
}

/**
 * Builds the trusted judge host set.
 *
 * The compiled-in default is always trusted: a `prod` build resolves it to
 * atbash.ai, a dev build to whatever DEV_ENDPOINT was set at build time. No
 * dev host is spelled out in source, and dev builds still validate their own
 * default.
 *
 * Exported for tests only. The set is a build-time value and is deliberately
 * never read from the process environment — an env var would let anyone widen
 * the allowlist of an already-shipped artifact, which is the silent-redirection
 * attack the allowlist exists to prevent (F-003). Asserting that requires
 * calling this with the environment set, so it cannot stay module-private.
 *
 * @internal
 */
export function buildAllowedJudgeHosts(): ReadonlySet<string> {
  return new Set([
    "atbash.ai",
    "www.atbash.ai",
    new URL(DEFAULT_ENDPOINT).hostname.toLowerCase(),
  ]);
}

const ALLOWED_JUDGE_HOSTS: ReadonlySet<string> = buildAllowedJudgeHosts();

export function validateJudgeEndpoint(
  judge?: JudgeEndpointConfig,
): ValidatedEndpoint {
  const policy: "default" | "self-hosted" =
    judge?.policy === "self-hosted" ? "self-hosted" : "default";
  const candidate = judge?.endpoint?.trim() || DEFAULT_ENDPOINT;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error(
      `[atbash] invalid judge endpoint URL: ${candidate}. ` +
        `Refusing to load — fix the URL or omit it to use the default (${DEFAULT_ENDPOINT}).`,
    );
  }

  // Loopback carve-out: http://localhost or http://127.0.0.1 traffic
  // never leaves the machine, so the MITM threat model that motivates
  // the https-only rule doesn't apply. Needed for local-dev testing
  // against a locally-running dashboard.
  const isLoopback =
    parsed.protocol === "http:" &&
    (parsed.hostname === "localhost" ||
      parsed.hostname === "127.0.0.1" ||
      parsed.hostname === "[::1]" ||
      parsed.hostname === "::1");
  if (parsed.protocol !== "https:" && !isLoopback) {
    throw new Error(
      `[atbash] judge endpoint must use https:// (got "${parsed.protocol}"). ` +
        `Refusing to load — plaintext endpoints leak verdicts and enable trivial MITM bypass.`,
    );
  }

  if (parsed.username || parsed.password) {
    throw new Error(
      `[atbash] judge endpoint must not contain credentials (user:pass@host). ` +
        `Refusing to load — credentials embedded in URLs leak to logs and process listings.`,
    );
  }

  const normalisedUrl = parsed.origin;

  if (policy === "self-hosted") {
    const verifyPubKey = (judge as { verifyPubKey?: string } | undefined)
      ?.verifyPubKey;
    const key = verifyPubKey?.trim().toLowerCase();
    if (!key || !/^[0-9a-f]{66}$/.test(key)) {
      throw new Error(
        `[atbash] judge endpoint policy "self-hosted" requires verifyPubKey ` +
          `to be a 66-hex-char compressed secp256k1 pubkey. Refusing to load — ` +
          `self-hosted judges must produce signed responses so the SDK can ` +
          `detect a malicious or compromised judge.`,
      );
    }
    return { url: normalisedUrl, policy, verifyPubKey: key };
  }

  // Loopback carve-out mirrors the http one above — traffic to localhost /
  // 127.0.0.1 / ::1 never traverses an untrusted network, so the
  // endpoint-redirection threat (F-003) doesn't apply.
  if (!isLoopback && !ALLOWED_JUDGE_HOSTS.has(parsed.hostname.toLowerCase())) {
    throw new Error(
      `[atbash] judge endpoint hostname "${parsed.hostname}" is not in the trusted allowlist. ` +
        `Allowed: ${[...ALLOWED_JUDGE_HOSTS].join(", ")}. ` +
        `To use a self-hosted judge, set BOTH policy="self-hosted" AND verifyPubKey ` +
        `to the 66-hex pubkey of your judge's response-signing key. ` +
        `Refusing to load — silent endpoint redirection is a known attack vector (F-003).`,
    );
  }

  return { url: normalisedUrl, policy, verifyPubKey: null };
}
